import React from "react";
import { Badge } from "../badge";
import { detalleEventoCliente } from "../../utils/clientes";

const fmtMoney = (v) => "$" + (Number(v) || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });
const fmtPct = (v) => `${Math.round(v * 100)}%`;

const Bloque = ({ title, children }) => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/60 p-3">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-zinc-500 mb-2">{title}</div>
    {children}
  </div>
);

const Vacio = ({ children }) => <p className="text-xs text-zinc-400 italic">{children}</p>;

// El renglón del evento, abierto: qué le cobramos, qué nos costó, con quién
// lo trabajamos y quién lo trajo. Es solo lectura — para editar cualquiera
// de esas cosas se entra al evento, y por eso no hay un botón aquí que
// prometa lo contrario.
//
// Los datos los junta `detalleEventoCliente` (puro, con pruebas); este
// componente solo pinta. Cada bloque aguanta estar vacío: un evento viejo
// sin cotización, sin costos o sin proveedores sigue siendo legible.
const ClienteEventoDetalle = ({ event, costos, proveedores, cancelado = false }) => {
  const d = detalleEventoCliente({ event, costos, proveedores });
  const proveedoresNoPlanner = d.proveedores.filter((p) => !p.esPlanner);

  return (
    <div className="space-y-3">
      {/* La fila de arriba pone el valor de un cancelado en "—" porque no
          suma al historial; aquí sí se ve lo que traía, y hay que decir
          por qué las dos cifras no son la misma. */}
      {cancelado && (
        <p className="text-xs text-zinc-500 italic">
          Evento cancelado: lo de abajo es lo que se había cotizado y gastado, y no cuenta en los totales del cliente.
        </p>
      )}
      {/* De un vistazo: entró, salió, quedó. */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {[
          { label: "Valor", value: fmtMoney(d.valor) },
          { label: "Costos", value: fmtMoney(d.totalCostos) },
          {
            label: "Utilidad",
            value: fmtMoney(d.utilidad),
            color: d.utilidad < 0 ? "text-red-600" : "text-emerald-600",
          },
          { label: "Margen", value: d.margen == null ? "—" : fmtPct(d.margen) },
        ].map((k) => (
          <div key={k.label}>
            <span className="text-zinc-400">{k.label}: </span>
            <span className={`font-semibold ${k.color || "text-zinc-900 dark:text-zinc-100"}`}>{k.value}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Lo que le cobramos */}
        <Bloque title="Conceptos cobrados">
          {d.conceptos.length === 0 ? (
            <Vacio>
              {d.valor > 0
                ? `Sin cotización desglosada — el valor viene del precio acordado (${fmtMoney(d.valor)}).`
                : "Este evento todavía no tiene cotización ni precio acordado."}
            </Vacio>
          ) : (
            <table className="min-w-full text-xs">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {d.conceptos.map((l) => (
                  <tr key={l.id}>
                    <td className="py-1.5 pr-2 text-zinc-700 dark:text-zinc-200">{l.concepto}</td>
                    <td className="py-1.5 px-2 text-right whitespace-nowrap text-zinc-400">
                      {l.units} × {fmtMoney(l.unitPrice)}
                    </td>
                    <td className="py-1.5 pl-2 text-right whitespace-nowrap font-medium text-zinc-900 dark:text-zinc-100">
                      {fmtMoney(l.importe)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-zinc-200 dark:border-zinc-700/60">
                {d.ivaEnabled && (
                  <>
                    <tr>
                      <td colSpan={2} className="py-1 pr-2 text-right text-zinc-400">Subtotal</td>
                      <td className="py-1 pl-2 text-right whitespace-nowrap text-zinc-500">{fmtMoney(d.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="py-1 pr-2 text-right text-zinc-400">IVA</td>
                      <td className="py-1 pl-2 text-right whitespace-nowrap text-zinc-500">{fmtMoney(d.iva)}</td>
                    </tr>
                  </>
                )}
                <tr>
                  <td colSpan={2} className="py-1 pr-2 text-right font-semibold text-zinc-500">Total</td>
                  <td className="py-1 pl-2 text-right whitespace-nowrap font-bold text-zinc-900 dark:text-white">
                    {fmtMoney(d.totalCobrado)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </Bloque>

        {/* Lo que nos costó */}
        <Bloque title="Costos del evento">
          {d.costos.length === 0 ? (
            <Vacio>Sin costos registrados en este evento.</Vacio>
          ) : (
            <table className="min-w-full text-xs">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {d.costos.map((c) => (
                  <tr key={c.id}>
                    <td className="py-1.5 pr-2">
                      <div className="text-zinc-700 dark:text-zinc-200">{c.concepto || "Sin concepto"}</div>
                      <div className="text-[11px] text-zinc-400">
                        {[c.categoria, c.proveedorNombre, (c.estado || "por_pagar") === "pagado" ? c.metodo : null]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </div>
                    </td>
                    <td className="py-1.5 pl-2 text-right whitespace-nowrap align-top">
                      <span className="font-medium text-zinc-900 dark:text-zinc-100">{fmtMoney(c.monto)}</span>
                      {(c.estado || "por_pagar") === "por_pagar" && (
                        <div className="mt-0.5">
                          <Badge color="amber">Por pagar</Badge>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-zinc-200 dark:border-zinc-700/60">
                <tr>
                  <td className="py-1 pr-2 text-right font-semibold text-zinc-500">Total</td>
                  <td className="py-1 pl-2 text-right whitespace-nowrap font-bold text-zinc-900 dark:text-white">
                    {fmtMoney(d.totalCostos)}
                  </td>
                </tr>
                {d.porPagar > 0 && (
                  <tr>
                    <td className="py-0.5 pr-2 text-right text-zinc-400">Por pagar</td>
                    <td className="py-0.5 pl-2 text-right whitespace-nowrap text-amber-600 font-medium">
                      {fmtMoney(d.porPagar)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          )}
        </Bloque>

        {/* Quién lo trabajó */}
        <Bloque title="Proveedores">
          {proveedoresNoPlanner.length === 0 ? (
            <Vacio>Sin proveedores asignados.</Vacio>
          ) : (
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {proveedoresNoPlanner.map((p) => (
                <li key={p.id} className="flex items-start gap-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-zinc-800 dark:text-zinc-100 truncate">{p.nombre}</div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {[p.servicio && (p.cantidad > 1 ? `${p.servicio} ×${p.cantidad}` : p.servicio), p.categoria]
                        .filter(Boolean)
                        .join(" · ") || "Sin servicio contratado"}
                    </div>
                  </div>
                  {p.costo > 0 && (
                    <span className="shrink-0 text-xs text-zinc-500 whitespace-nowrap">{fmtMoney(p.costo)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Bloque>

        {/* Quién lo trajo */}
        <Bloque title="Planner">
          {!d.planner ? (
            <Vacio>Este evento llegó sin planner.</Vacio>
          ) : (
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-zinc-800 dark:text-zinc-100 truncate">{d.planner.nombre}</div>
                <div className="text-[11px] text-zinc-400">
                  {d.planner.comision ? "Comisión del evento" : "Sin comisión registrada en este evento"}
                </div>
              </div>
              {d.planner.comision && (
                <div className="shrink-0 text-right">
                  <div className="text-xs font-medium text-zinc-900 dark:text-zinc-100 whitespace-nowrap">
                    {fmtMoney(d.planner.comision.monto)}
                  </div>
                  {d.planner.comision.estado !== "pagado" && (
                    <Badge color="amber">Por pagar</Badge>
                  )}
                </div>
              )}
            </div>
          )}
        </Bloque>
      </div>
    </div>
  );
};

export default ClienteEventoDetalle;
